import { input } from '@inquirer/prompts'
import { ArgumentsCamelCase } from 'yargs'
import { testConnection } from './testConnection'
import { getTargetUrlAndAccessToken, setTargetUrlAndAccessToken } from './accessTokens'
import { shouldLogin, whichInstancePrompt } from './prompts'
import { checkIfNewerVersionAvailableAndLog } from '../../util'

export type LoginType = 'default' | 'sync-target'

export const doLogin = async (
  askForUnixSocket: boolean,
  headers: string[],
  loginType: LoginType = 'default',
  url?: string,
  pathToUnixSocket?: string,
): Promise<{
  url: string
  accessToken: string
  unixSocket: string | undefined
  error: string | undefined
}> => {
  if (!url) {
    url = (await whichInstancePrompt(loginType)).url
  }

  const accessToken = await input({
    message: `What is the access token you would like to use (${url}/settings/api)?`,
  })

  let socketGiven: string | undefined
  if(pathToUnixSocket){
    socketGiven = pathToUnixSocket
  } else {
    socketGiven = askForUnixSocket
      ? await input({
        message: `What is the socket you would like to use? Give an empty answer to skip using a unix socket`,
      })
    : undefined
  }

  const socketProccessed = socketGiven && socketGiven.length > 0 ? socketGiven : undefined

  const errorMessage = await testConnection({
    url,
    accessToken,
    headers,
    unixSocket: socketProccessed,
  })

  if (errorMessage) {
    console.error(`An error happened while connecting to the Retool backend:\n - ${errorMessage}`)
    return {
      url,
      accessToken,
      error: errorMessage,
      unixSocket: socketProccessed,
    }
  } else {
    setTargetUrlAndAccessToken({
      url,
      accessToken,
      unixSocket: socketProccessed,
      instance: loginType !== 'default' ? url : 'default',
    })
    return {
      url,
      accessToken,
      error: undefined,
      unixSocket: socketProccessed,
    }
  }
}

export const loginCommand = async (
  argv: ArgumentsCamelCase<{ status: boolean; 'unix-socket': boolean, 'skip-updates-check': boolean, header?: string[] }>,
): Promise<void> => {
  if (argv['status']) {
    const targetUrlAndAccessToken = getTargetUrlAndAccessToken()
    if (targetUrlAndAccessToken) {
      console.log(`Currently logged in to ${targetUrlAndAccessToken.url}.`)
    } else {
      console.log('Not currently logged in')
    }
  } else {
    if (await shouldLogin()) {
      await doLogin(argv['unix-socket'], argv.header ?? [], 'default')
    }
  }

  await checkIfNewerVersionAvailableAndLog(argv['skip-updates-check'])
}
